import { useCallback, useEffect, useRef, useState } from 'react';
import { Badge, Button, List, Message, Space, Typography } from '@arco-design/web-react';
import { IconClose, IconNotification } from '@arco-design/web-react/icon';
import { useNavigate } from 'react-router-dom';
import { listNotifications, readAllNotifications, readNotification, unreadNotificationCount, type Notification } from '../api/notification';
import { fetchNoticeUnreadCount, fetchPendingNotices, type PendingNotice } from '../api/notice';
import { listMyWorkflowTasks, type WorkflowTask } from '../api/workflow';
import { fmtTime } from '../utils/format';
import { notificationTypeLabel } from '../utils/labels';
import { refRoute } from '../utils/notifyRoute';
import BccTabs, { BccTabPane } from './BccTabs';
import PillTag from './PillTag';
import { listEmptyNode, ListScroll, PanelFooter } from './NotificationBell/PanelParts';

/**
 * WS 触发刷新的合并窗口（ms）：notify/workflow 事件可能成串到达（一瞬几十条），
 * 逐条刷新会把每个事件放大成 2~3 次 HTTP 请求造成请求风暴；窗口内合并为一次尾随刷新。
 */
const WS_REFRESH_DEBOUNCE_MS = 1000;

/** 铃铛面板三个页签的全量出口（同一页面的三个页签：待办 / 通知 / 公告） */
const INBOX_ROUTE = '/workbench/tasks';

/**
 * 通知铃：待办 / 通知 / 公告三页签 + 未读角标（服务端计数）+ 全部已读。
 *
 * **面板是「提醒面」，不是历史面**：待办只列待处理的流程任务、通知只列未读、公告只列待完成——
 * 所以页签内**不再挂档位筛选**（那是与页签同轴的第二排按钮，视觉重复且与全量页重复）。
 * 全量清单有唯一正本：待办中心（`INBOX_ROUTE`，页内三页签与这里一一对应，通知/公告自带筛选 + 分页），
 * 每个页签底部的「查看全部 …→」按 `?tab=` 落到对应页签。
 * 外部可调用 refresh() 在收到 WS notify 事件时刷新。
 */
export default function NotificationBell() {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState('todo');
  const [items, setItems] = useState<Notification[]>([]);
  const [unread, setUnread] = useState(0);
  const [loading, setLoading] = useState(false);
  // 待办（workflow pending 任务）
  const [todos, setTodos] = useState<WorkflowTask[]>([]);
  const [todoTotal, setTodoTotal] = useState(0);
  const [todosLoading, setTodosLoading] = useState(false);
  // 公告：待完成（本人未完成的生效中公告）
  const [pending, setPending] = useState<PendingNotice[]>([]);
  const [noticeUnread, setNoticeUnread] = useState(0);
  const [pendingLoading, setPendingLoading] = useState(false);
  const refreshRef = useRef<() => void>(() => {});
  refreshRef.current = refresh;
  // WS 高频事件合并：窗口内多条 notify/workflow 事件只触发一次请求（防请求风暴）
  const notifyTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const todosTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  /**
   * 在途请求序号：同一份清单有三路入口（挂载 refresh / 点开面板 onToggle / WS 合并刷新），
   * 并发同参请求乱序落地本身只影响"最后写入者"，真正会出错的是两处状态被迟到响应改回去：
   * ① loading 被更早的响应提前清掉；② 乐观摘除（点开置读 / 全部已读）被一个更早发出、
   * 仍含已点项的 res.list 回滚（通知重新出现在未读列表里）。故落地前判序号是否仍是最新。
   */
  const noticeSeqRef = useRef(0);
  const todosSeqRef = useRef(0);
  const announceSeqRef = useRef(0);

  async function refresh() {
    await loadNotifications();
    await loadAnnounce();
  }

  /** 未读通知（面板只列未读；已读的去处是消息中心） */
  async function loadNotifications() {
    const seq = ++noticeSeqRef.current;
    setLoading(true);
    try {
      const [res, unreadRes] = await Promise.all([
        listNotifications({ read: false, page: 1, pageSize: 20 }),
        unreadNotificationCount().catch(() => 0),
      ]);
      if (seq !== noticeSeqRef.current) return; // 已有更新的请求发出：丢弃过期清单，别回滚本地摘除
      setItems(res.list || []);
      setUnread(unreadRes);
    } catch { /* 拦截器已提示 */ } finally {
      if (seq === noticeSeqRef.current) setLoading(false);
    }
  }

  /** 本地即改即摘（点开置读 / 全部已读）后作废在途清单响应，并顺手收掉 loading：
   *  在途响应已经被作废，其 finally 不会再复位 loading，必须在这里归零（否则列表永久转圈）。 */
  function invalidateNotifications() {
    noticeSeqRef.current++;
    setLoading(false);
  }

  const loadTodos = useCallback(async () => {
    const seq = ++todosSeqRef.current;
    setTodosLoading(true);
    try {
      const { list, total } = await listMyWorkflowTasks({ status: 'pending', page: 1, pageSize: 20 });
      if (seq !== todosSeqRef.current) return;
      setTodos(list || []);
      setTodoTotal(total);
    } catch { /* 拦截器已提示 */ } finally {
      if (seq === todosSeqRef.current) setTodosLoading(false);
    }
  }, []);

  /** 公告（待完成）：面板只列待完成，已完成/已失效在消息中心 */
  const loadAnnounce = useCallback(async () => {
    const seq = ++announceSeqRef.current;
    setPendingLoading(true);
    try {
      const [list, unread] = await Promise.all([fetchPendingNotices(), fetchNoticeUnreadCount()]);
      if (seq !== announceSeqRef.current) return;
      setPending(list || []);
      setNoticeUnread(unread);
    } catch { /* 拦截器已提示 */ } finally {
      if (seq === announceSeqRef.current) setPendingLoading(false);
    }
  }, []);

  // 未完成公告数（角标）与面板开合无关，收起时也要准
  const loadNoticeUnread = useCallback(async () => {
    try {
      setNoticeUnread(await fetchNoticeUnreadCount());
    } catch { /* 拦截器已提示 */ }
  }, []);

  // WS 触发入口：窗口内合并（尾随一次），供 BasicLayout 的 notify/workflow 事件调用；
  // 挂载/点开铃铛走 refresh() 即时拉取不受影响
  const refreshCoalesced = useCallback(() => {
    if (notifyTimer.current) clearTimeout(notifyTimer.current);
    notifyTimer.current = setTimeout(() => {
      notifyTimer.current = undefined;
      refreshRef.current();
    }, WS_REFRESH_DEBOUNCE_MS);
  }, []);
  const refreshTodosCoalesced = useCallback(() => {
    if (todosTimer.current) clearTimeout(todosTimer.current);
    todosTimer.current = setTimeout(() => {
      todosTimer.current = undefined;
      loadTodos();
    }, WS_REFRESH_DEBOUNCE_MS);
  }, [loadTodos]);

  // 挂载即拉一次；暴露到 window 供 WS 事件刷新（通知 tab / 待办 tab 分开暴露，
  // v1.10 workflow 事件到达时两路都刷新）
  useEffect(() => {
    void refresh();
    void loadTodos();
    void loadNoticeUnread();
    (window as any).__refreshNotifications = refreshCoalesced;
    (window as any).__refreshTodos = refreshTodosCoalesced;
    (window as any).__refreshNotices = () => { void loadNoticeUnread(); void loadAnnounce(); };
    return () => {
      delete (window as any).__refreshNotifications;
      delete (window as any).__refreshTodos;
      delete (window as any).__refreshNotices;
      if (notifyTimer.current) clearTimeout(notifyTimer.current);
      if (todosTimer.current) clearTimeout(todosTimer.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshCoalesced, refreshTodosCoalesced, loadAnnounce, loadNoticeUnread]);

  const onToggle = () => {
    setOpen((o) => {
      if (!o) {
        // 每次展开都重新对账（面板只列未读/待处理，点过的项必须已经不在里面）
        void loadNotifications();
        void loadTodos();
        void loadAnnounce();
        void loadNoticeUnread();
      }
      return !o;
    });
  };

  const onSwitchTab = (k: string) => {
    setTab(k);
    if (k === 'notice') void loadNotifications();
    if (k === 'todo') void loadTodos();
    if (k === 'announce') { void loadAnnounce(); void loadNoticeUnread(); }
  };

  const onClickItem = async (n: Notification) => {
    // 面板只列未读：点开即从清单里摘掉（置读失败也不留一条已读项，下次展开会与服务端重新对账）
    invalidateNotifications(); // 先作废在途清单响应，否则它落地时会把刚摘掉的这条塞回未读列表
    setUnread((u) => Math.max(0, u - 1));
    setItems((list) => list.filter((x) => x.id !== n.id));
    readNotification(n.id).catch(() => {});
    setOpen(false);
    // P7：点击跳转 ref（有映射才跳转）
    const route = refRoute(n.ref_type, n.ref_id);
    if (route) navigate(route);
  };

  const onReadAll = async () => {
    invalidateNotifications(); // 同上：全量已读后在途清单响应落地会把刚清空的未读列表又塞回来
    setUnread(0);
    setItems([]); // 只列未读 → 全部已读后清单即空
    try {
      await readAllNotifications();
      Message.success('已全部标记为已读');
    } catch { /* 拦截器已提示 */ }
  };

  /** 全量出口：待办中心对应页签（通知 / 公告） */
  const goMessages = (t: 'notice' | 'announce') => {
    setOpen(false);
    navigate(`${INBOX_ROUTE}?tab=${t}`);
  };

  return (
    <Badge count={unread + todoTotal + noticeUnread} maxCount={99} offset={[2, 4]}>
      <Button
        shape="circle"
        type="text"
        icon={<IconNotification />}
        onClick={onToggle}
        style={{ color: 'var(--color-text-1)' }}
      />
      {open && (
        <>
          {/* 点击外部关闭的透明捕捉层：置于 BasicLayout 高危风险卡（zIndex 1001）之上，
              铃铛开着时点风险卡区域也先关面板（风险内容同时已进「通知」列表，关闭后卡片完整可操作） */}
          <div
            onClick={() => setOpen(false)}
            style={{ position: 'fixed', inset: 0, zIndex: 1002, cursor: 'default' }}
          />
          <div
            style={{
              position: 'fixed',
              top: 56,
              right: 24,
              width: 380,
              maxHeight: 520,
              overflow: 'auto',
              background: 'var(--color-bg-2)',
              border: '1px solid var(--color-border-2)',
              borderRadius: 8,
              boxShadow: 'var(--bcc-shadow-float)',
              // 叠置修正：面板与风险卡同锚右上（right:24），风险卡 z1001 会覆盖本面板页签区；
              // 面板为活动表面抬至 1003（>风险卡 1001），关闭后风险卡恢复最上层阻断展示
              zIndex: 1003,
              padding: 8,
            }}
          >
            <BccTabs
              activeTab={tab}
              onChange={onSwitchTab}
              size="small"
              contentPadding={false}
              extra={
                <Space size={0}>
                  {tab === 'notice' && unread > 0 && (
                    <Button size="mini" type="text" onClick={onReadAll}>
                      全部已读
                    </Button>
                  )}
                  <Button size="mini" type="text" icon={<IconClose />} onClick={() => setOpen(false)} aria-label="关闭通知" />
                </Space>
              }
            >
              <BccTabPane key="todo" title={`待办${todoTotal ? `(${todoTotal})` : ''}`}>
                <ListScroll>
                  <List
                    loading={todosLoading}
                    size="small"
                    dataSource={todos}
                    noDataElement={listEmptyNode(todosLoading, '暂无待办')}
                    render={(t) => (
                      <List.Item
                        key={t.id}
                        onClick={() => {
                          setOpen(false);
                          navigate(`${INBOX_ROUTE}?tab=todo`);
                        }}
                        style={{ cursor: 'pointer', padding: '6px 8px' }}
                      >
                        <List.Item.Meta
                          title={
                            <Space size={6}>
                              <span style={{ color: 'rgb(var(--danger-6))' }}>●</span>
                              <Typography.Text style={{ fontSize: 13 }}>{t.node_name}</Typography.Text>
                              {t.sla_due_at && (
                                <PillTag size="sm" color="orange">
                                  SLA {fmtTime(t.sla_due_at)}
                                </PillTag>
                              )}
                            </Space>
                          }
                          description={
                            <Typography.Text type="secondary" style={{ fontSize: 11, display: 'block' }}>
                              流程任务 #{t.instance_id} · 创建于 {fmtTime(t.created_at)}
                            </Typography.Text>
                          }
                        />
                      </List.Item>
                    )}
                  />
                </ListScroll>
                <PanelFooter>
                  <Button size="mini" type="text" onClick={() => { setOpen(false); navigate(`${INBOX_ROUTE}?tab=todo`); }}>
                    查看全部待办 →
                  </Button>
                </PanelFooter>
              </BccTabPane>
              <BccTabPane key="notice" title={`通知${unread ? `(${unread})` : ''}`}>
                <ListScroll>
                  <List
                    loading={loading}
                    size="small"
                    dataSource={items}
                    noDataElement={listEmptyNode(loading, '暂无未读通知')}
                    render={(n) => (
                      <List.Item
                        key={n.id}
                        onClick={() => onClickItem(n)}
                        style={{ cursor: 'pointer', padding: '6px 8px', background: 'rgba(var(--primary-1), 0.4)' }}
                      >
                        <List.Item.Meta
                          title={
                            <Space size={6} wrap>
                              <span style={{ color: 'rgb(var(--danger-6))' }}>●</span>
                              <PillTag size="sm" color="arcoblue">
                                {notificationTypeLabel(n.type)}
                              </PillTag>
                              {n.level === 'critical' && (
                                <PillTag size="sm" color="red">
                                  高危
                                </PillTag>
                              )}
                              <Typography.Text style={{ fontSize: 13 }}>{n.title}</Typography.Text>
                            </Space>
                          }
                          description={
                            <div>
                              {n.body && <Typography.Text style={{ fontSize: 12 }}>{n.body}</Typography.Text>}
                              {n.created_at && (
                                <Typography.Text type="secondary" style={{ fontSize: 11, display: 'block' }}>
                                  {fmtTime(n.created_at)}
                                </Typography.Text>
                              )}
                            </div>
                          }
                        />
                      </List.Item>
                    )}
                  />
                </ListScroll>
                <PanelFooter>
                  <Button size="mini" type="text" onClick={() => goMessages('notice')}>
                    查看全部通知 →
                  </Button>
                </PanelFooter>
              </BccTabPane>
              <BccTabPane key="announce" title={`公告${noticeUnread ? `(${noticeUnread})` : ''}`}>
                <ListScroll>
                  <List
                    loading={pendingLoading}
                    size="small"
                    dataSource={pending}
                    noDataElement={listEmptyNode(pendingLoading, '暂无待完成公告')}
                    render={(p) => (
                      <List.Item
                        key={p.notice.id}
                        onClick={() => {
                          setOpen(false);
                          Message.info('该公告将在下次登录时强制完成');
                        }}
                        style={{ cursor: 'pointer', padding: '6px 8px', background: 'rgba(var(--primary-1), 0.4)' }}
                      >
                        <List.Item.Meta
                          title={
                            <Space size={6}>
                              <span style={{ color: 'rgb(var(--danger-6))' }}>●</span>
                              <PillTag size="sm" color="orangered">
                                待完成
                              </PillTag>
                              <Typography.Text style={{ fontSize: 13 }}>{p.notice.title}</Typography.Text>
                            </Space>
                          }
                          description={
                            <Typography.Text type="secondary" style={{ fontSize: 11, display: 'block' }}>
                              点击阅读并完成
                            </Typography.Text>
                          }
                        />
                      </List.Item>
                    )}
                  />
                </ListScroll>
                <PanelFooter>
                  <Button size="mini" type="text" onClick={() => goMessages('announce')}>
                    查看全部公告 →
                  </Button>
                </PanelFooter>
              </BccTabPane>
            </BccTabs>
          </div>
        </>
      )}
    </Badge>
  );
}
